import { useState, useCallback } from 'react'
import { message } from 'antd'
import { ORDER_STATUS, PRIORITY_LEVELS, ERROR_MESSAGES } from '../utils/constants'
import { cancelLabOrder, createLabOrder, getResultsByOrderNumber, readLabOrders } from '../utils/utils'
import { downloadPDFFromBase64, generatePDFFilename } from '../utils/resultUtils'

export const useLabOrders = () => {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchOrders = useCallback(async (filters = {}) => {
    setLoading(true)
    setError(null)

    try {
      setLoading(true)
      const response = await readLabOrders(filters)
      setOrders(response)
    } catch (err) {
      setError(ERROR_MESSAGES.FETCH_ORDERS_FAILED)
      message.error(ERROR_MESSAGES.FETCH_ORDERS_FAILED)
    } finally {
      setLoading(false)
    }
  }, [])

  const createOrder = useCallback(async (data) => {
    setLoading(true)
    setError(null)

    try {
      setLoading(true)
      const newOrder = await createLabOrder(data)

      setOrders((prev) => [newOrder, ...prev])
      message.success('Lab order created successfully')
      return newOrder
    } catch (err) {
      setError(ERROR_MESSAGES.CREATE_ORDER_FAILED)
      message.error(ERROR_MESSAGES.CREATE_ORDER_FAILED)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const updateOrder = useCallback(async (orderId, updates) => {
    setLoading(true)
    setError(null)

    try {
      // TODO: Replace with actual API call
      // const response = await labOrdersApi.updateOrder(orderId, updates)
      // const updatedOrder = response.data

      // Mock implementation
      setOrders((prev) =>
        prev.map((order) =>
          order.id === orderId ? { ...order, ...updates, updatedAt: new Date().toISOString() } : order
        )
      )

      message.success('Order updated successfully')
    } catch (err) {
      setError(ERROR_MESSAGES.UPDATE_ORDER_FAILED)
      message.error(ERROR_MESSAGES.UPDATE_ORDER_FAILED)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const cancelOrder = useCallback(async (orderNumber, doctorId) => {
    setLoading(true)
    setError(null)

    try {
      setLoading(true)
      const response = await cancelLabOrder(orderNumber, doctorId)

      if (response.error && response.error !== '') {
        throw new Error(response.error)
      } else {
        setOrders((prev) =>
          prev.map((order) => (order.orderNumber === orderNumber ? { ...order, status: ORDER_STATUS.EXPIRED } : order))
        )

        message.success('Order cancelled successfully')
        return response
      }
    } catch (err) {
      setError(ERROR_MESSAGES.CANCEL_ORDER_FAILED)
      message.error(ERROR_MESSAGES.CANCEL_ORDER_FAILED)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const resultsByOrderNumber = useCallback(async (orderNumber, includePdf, reportType) => {
    setLoading(true)
    setError(null)

    try {
      setLoading(true)
      const response = await getResultsByOrderNumber(orderNumber, includePdf, reportType)

      if (response.error && response.error !== '') {
        throw new Error(response.error)
      } else {
        return response
      }
    } catch (err) {
      setError(ERROR_MESSAGES.FETCH_RESULTS_FAILED)
      message.error(ERROR_MESSAGES.FETCH_RESULTS_FAILED)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const refreshOrders = useCallback(
    async (filters = {}) => {
      await fetchOrders(filters)
    },
    [fetchOrders]
  )

  const getOrderById = useCallback(
    (orderId) => {
      return orders.find((order) => order.id === orderId)
    },
    [orders]
  )

  const getOrdersByStatus = useCallback(
    (status) => {
      return orders.filter((order) => order.status === status)
    },
    [orders]
  )

  const getPendingOrders = useCallback(() => {
    return orders.filter((order) => order.status === ORDER_STATUS.PENDING || order.status === ORDER_STATUS.IN_PROGRESS)
  }, [orders])

  const getUrgentOrders = useCallback(() => {
    return orders.filter((order) => order.priority === PRIORITY_LEVELS.URGENT)
  }, [orders])

  // Function to download PDF from base64 using utility
  const handleDownloadPDF = async (pdfData, orderNumber, messageId) => {
    try {
      const filename = generatePDFFilename(orderNumber, messageId)
      await downloadPDFFromBase64(pdfData, filename)
      message.success('PDF downloaded successfully')
    } catch (error) {
      console.error('Failed to download PDF:', error)
      message.error('Failed to download PDF')
    }
  }

  return {
    orders,
    loading,
    error,
    fetchOrders,
    createOrder,
    updateOrder,
    cancelOrder,
    refreshOrders,
    getOrderById,
    getOrdersByStatus,
    getPendingOrders,
    getUrgentOrders,
    resultsByOrderNumber,
    handleDownloadPDF
  }
}
